import React, { useState, useEffect, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import LoadItem from '../common/FlexiItem/FlexiItemLoader';
import FlexiItemTopMenu from '../common/FlexiItem/FlexiItemTopMenu';
import FlexiItemHeader from '../common/FlexiItem/FlexiItemHeader';
import FlexiItemContent from '../common/FlexiItem/FlexiItemContent';

import { AppContext } from '../Auth/contexts/AppContext';

// Redux
import { useDispatch } from 'react-redux';
import {
	setCurrentMenuSection,
	setCurrentMenuItem
} from '../../features/mainmenu/mainmenuSlice';

// CSS
import flexstyles from '../../css/FlexCss';
import useClasses from '../../ui/useClasses';

// Icons for custom buttons if needed
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

var dbendpoint = process.env.REACT_APP_DB_API4;

const FlexiInvItem = ({
	isChild,
	itemid,
	viewmode,
	backlinkurl,
	backlinktext,
	SetView,
	UpdateState: parentUpdateState
}) => {
	const classes = useClasses(flexstyles);
	const appContext = useContext(AppContext);
	const { userPerms, userRole, userName } = appContext;
	const history = useHistory();
	const dispatch = useDispatch();

	// URL param fallback for when this is called as a child view
	let { id } = useParams();
	if (!id && isChild) {
		id = itemid;
	}

	// ----------------------------
	// Permissions
	// ----------------------------
	const perms = {
		createPerm: (userPerms?.createItem === 1 ? true : false),
		readPerm: (userPerms?.readItem === 1 ? true : false),
		updatePerm: (userPerms?.updateItem === 1 ? true : false),
		deletePerm: (userPerms?.deleteItem === 1 ? true : false),
		admin: (userRole === "admin" ? true : false),
	};

	// ----------------------------
	// Helper functions for custom screen logic
	// ----------------------------
	const CalculateMargin = ({ localstate }) => {
		const cost = parseFloat(localstate.itemdata.Cost || 0);
		const price = parseFloat(localstate.itemdata.Price || 0);
		if (!isNaN(cost) && !isNaN(price)) {
			localstate.itemdata.Margin = (price - cost).toFixed(2);
		}
	};

	const UpdateDerivedFields = ({ localstate }) => {
		// Example derived values for Inventory Item views
		// Use this after data load or after certain changes.
		// Keep it simple and explicit.
		if (localstate.itemdata?.Price && localstate.itemdata?.Cost) {
			CalculateMargin({ localstate });
		}

		// Example: condition or grade based lookups
		// if (localstate.itemdata.Condition) { ... }
	};

	// ----------------------------
	// Configuration
	// ----------------------------
	const configuration = {
		menuitem: "/inventorymanager",
		menusection: "Inventory",
		title: "Inventory Item",

		headerprepend: "Item: ",
		headervalue: "SerialNumber", // or "SerialNumber" depending on your item naming convention
		headerappend: "",

		maxwidth: "1200px",
		defaulteditmode: true,

		fetchapiendpoint: "/flexiitem/getitem",
		updateapiendpoint: "/flexiitem/update",
		dbmodel: "flexitem",
		dbkey: "ItemID",
		dbkeyvalue: id,

		nestedrelationships: [
			"creationuser",
			"updateuser",
			"deletionuser",
			//"lastrepairuser", //Not really needed
			"product",
			"minorconditions",
			"majorconditions",
			"damagedconditions",
			"repairs",
			"mdm",
			"specs",
			"lot.vendor",
			"reviewuser",
			"manifests.order",
			
			//"location",
			//"vendor"
		],
		//These are things like tags, conditions, etc.
		editablechildren:[
			'minorconditions',
			'majorconditions',
			'damagedconditions',
			'repairs',
			'mdm',
		],

		childitemurl: "/item",
		parentitemurl: "/inventorymanager",
		shortposttimeout: 20000,
		longposttimeout: 60000,

		softdeletesupport: true,
		creationusersupport: true,
		updateusersupport: true,
		deleteusersupport: true,
		historysupport: false, // intentionally disabled per your direction

		createPerm: perms.createPerm,
		readPerm: perms.readPerm,
		updatePerm: perms.updatePerm,
		deletePerm: perms.deletePerm,
		auditPerm: false,

		mutatedata: null,
		exportmutatedata: null,

		datetimes: [
			"DateTime",
			"created_at",
			"updated_at",
			"LastAudit",
			"ReceivedDate",
			"LastAuditDate",
			"ReviewedDate",
			"LastRepairDate"
		],

		floats: [
			"Cost",
			"Price",
			"Margin",
			"WholesalePrice"
		],

		topmenubar: {
			render: true,
			backbutton: {
				render: true,
				nonchildlinkurl: "/inventorymanager",
				nonchildlinktext: "Go to Inventory"
			},
			savebutton: {
				render: true,
				text: "Apply Changes"
			},
			//Some point, create a print function for things rendered on page.
			printbutton: {
				render: false,
				text: "Print Record"
			},
			//Move Delete function somewhere else!
			deletebutton: {
				render: false,
				text: "Delete Item",
				confirmationtext: "Confirm deletion of item: "
			},
			statusbar: true,
			custombuttons: [
				{
					render: true,
					text: "Small Label",
					icon: null,
					buttonFn: ({ localstate }) => {
						var params = {
							itemid: localstate.itemdata.ItemID,
							labeltype: "inventory-sm",
							autoclose: true
						};
						// Creating a form
						var form = document.createElement("form");
						form.setAttribute("method", "post");
						form.setAttribute("action", dbendpoint + "/labels");
						form.setAttribute("target", "NewLabel");
						for (var i in params) {
							if (params.hasOwnProperty(i)) {
								// Creating the input
								var input = document.createElement('input');
								input.type = 'hidden';
								input.name = i;
								input.value = params[i];

								// Attach input to form
								form.appendChild(input);
							}
						}
						document.body.appendChild(form);
						form.submit();
					}
				},
				{
					render: true,
					text: "Large Label",
					icon: null,
					buttonFn: ({ localstate }) => {
						var params = {
							itemid: localstate.itemdata.ItemID,
							labeltype: "inventory",
							autoclose: true
						};
						// Creating a form
						var form = document.createElement("form");
						form.setAttribute("method", "post");
						form.setAttribute("action", dbendpoint + "/labels");
						form.setAttribute("target", "NewLabel");
						for (var i in params) {
							if (params.hasOwnProperty(i)) {
								// Creating the input
								var input = document.createElement('input');
								input.type = 'hidden';
								input.name = i;
								input.value = params[i];

								// Attach input to form
								form.appendChild(input);
							}
						}
						document.body.appendChild(form);
						form.submit();
					}

				},
			]
		},

		// ----------------------------
		// Content ordering mirrors the old Item.js style
		// ----------------------------
		contentelements: [
			{
				rendertype: "section",
				title: "Item Details",
				description: "",
				children: [
					{
						rendertype: "gridcontainer",
						children: [
							{
								rendertype: "griditem",
								size: { xs: 12, sm: 12, md: 6, lg: 6 },
								children: [
									//Input serial allows creation users to create a new serial number (should validate unique) (TODO)
									//Avoid mutation even by update users! Only admins and people that can create should be able to make a serialnumber.
									{
										rendertype:"kvinputserial",
										dbfield:"SerialNumber",
										label:"Serial Number:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										validation:{
											type:"text",
											maxlength:"100",
											minlength:"1"
										}
									},
									{
										rendertype: "kvinputtext",
										dbfield: "Mfg",
										label: "Mfg:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										}
									},
									{
										rendertype: "kvinputtext",
										dbfield: "Model",
										label: "Model:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										},
										enabledrequirements:[
											{key:"ProductID", values:[null, 0]}
										]
									},
									{
										rendertype:"kvproductbyid", //We don't store Product Name inside records. We store the Product ID. The input shows the product name from the relationship, but a function updates the ProductID.
										dbfield:"product",
										label:"Product:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
								]
							},
							{
								rendertype: "griditem",
								size: { xs: 12, sm: 12, md: 6, lg: 6 },
								children: [
									{
										rendertype:"kvstaticselect",
										dbfield:"Status",
										label:"Status:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										disableonvalues:[],
										values:[
											{ optionlabel:"Checked In", value:"Checked In"},
											{ optionlabel:"Sold", value: "Sold"},
											{ optionlabel:"Scrap", value:"Scrap"},
											{ optionlabel:"Returned to Vendor", value:"Returned to Vendor"},
										],
										validation:{
											type:"select",
											maxlength:"100",
											minlength:"1"
										}
									},
									{
										rendertype: "kvinputtext",
										dbfield: "Location",
										label: "Location:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										}
									},
									{
										rendertype: "kvinputnumber",
										dbfield: "Cost",
										label: "Cost:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										step: "0.01",
										validation: {
											type: "number",
											min: 0,
											max: 1000000000000
										},
										postChangeHandler: CalculateMargin
									},
									{
										rendertype: "kvinputtext",
										dbfield: "AssetType",
										label: "Asset Type:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										}
									},
								]
							}
						]
					}
				]
			},

			{
				rendertype: "section",
				title: "Condition",
				description: "",
				children: [
					{
						rendertype: "gridcontainer",
						children: [
							{
								rendertype: "griditem",
								size: { xs: 12, sm: 12, md: 6, lg: 6 },
								children: [
									
									{
										rendertype:"kvinputbuttongroup",
										dbfield:"Grade",
										label:"Grade:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										values:[
											//Build in any blank string options here:
											{ optionlabel:"A", value:"A"},
											{ optionlabel:"B", value: "B"},
											{ optionlabel:"C", value:"C"},
											{ optionlabel:"Repair", value:"Repair"},
											{ optionlabel:"Scrap", value:"Scrap"},
										],
										validation:{
											type:"text"
										}
									},
									{
										rendertype:"kvinputbuttongroup",
										dbfield:"BodyGrade",
										label:"Body Grade:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										//New Concept: Render Requirements. Introduced with some asset types being "Laptop" and needing more grading inputs!
										renderrequirements:[
											{key:"AssetType", values:["Laptop","AIO"]}, //Or perhaps values:["Laptop","AIO"] - both have LCDs to grade! Or-Able!
											//You can add more levels of requirements, but if it fails any level, it won't render.
											//{key:"Status", values:["Checked In", "Sold"]} //Example only
											//{key:"SerialNumber", values:[3334]}
										],
										values:[
											//Build in any blank string options here:
											{ optionlabel:"A", value:"A"},
											{ optionlabel:"B", value: "B"},
											{ optionlabel:"C", value:"C"},
											{ optionlabel:"Bad", value:"Bad"},
										],
										validation:{
											type:"text"
										}
									},
									{
										rendertype:"kvinputbuttongroup",
										dbfield:"LCDGrade",
										label:"LCD Grade:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										//New Concept: Render Requirements. Introduced with some asset types being "Laptop" and needing more grading inputs!
										renderrequirements:[
											{key:"AssetType", values:["Laptop","AIO"]}, //Or perhaps values:["Laptop","AIO"] - both have LCDs to grade! Or-Able!
											//You can add more levels of requirements, but if it fails any level, it won't render.
											//{key:"Status", values:["Checked In", "Sold"]} //Example only
											//{key:"SerialNumber", values:[3334]}
										],
										values:[
											//Build in any blank string options here:
											{ optionlabel:"A", value:"A"},
											{ optionlabel:"B", value: "B"},
											{ optionlabel:"C", value:"C"},
											{ optionlabel:"Bad", value:"Bad"},
										],
										validation:{
											type:"text"
										}
									},	
									{
										rendertype:"kvinputtextarea",
										dbfield:"Notes",
										label:"Notes:",
										labelwidth:170,
										height:"45px",
										labeltextAlign:"right",
										valuetextAlign:"left",
										backgroundColor:"#EEE",
										validation:{
											type:"text"
										}
									},
								]
							},
							{
								rendertype: "griditem",
								size: { xs: 12, sm: 12, md: 6, lg: 6 },
								children: [
									{
										rendertype:"kvheader",
										dbfield:"",
										label:"Current Conditions",
										labelwidth:170,
										padding:"5px 0px 4px 0px",
										labeltextAlign:"center",
										valuetextAlign:"left",
									},	
									{
										rendertype:"kvrendertags",
										dbfield:"minorconditions", //When we go to save the item, we'll need to have an idea of children!
										label:"Minor:",
										tagkey:"ConditionText", //Human readable value/column - the thing we type in and change on the child row. "ConditionText"
										defaultstyle:{borderRadius:3, color:"#FFF"},
										conditionalstyles:[
											{key:"ConditionType", value:"Minor", backgroundColor:"#CCB136", color:"#FFF", border:"1px solid #ada100"},
										],
										inline:true, //Allow items to show in-line or not.
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										allowdeletion:true,
									},
									{
										rendertype:"kvrendertags",
										dbfield:"majorconditions", //When we go to save the item, we'll need to have an idea of children!
										label:"Major:",
										tagkey:"ConditionText", //Human readable value/column - the thing we type in and change on the child row. "ConditionText"
										defaultstyle:{borderRadius:3, color:"#FFF"},
										conditionalstyles:[
											{key:"ConditionType", value:"Major", backgroundColor:"#f0ad4e", color:"#FFF", border:"1px solid #eea236"},
										],
										inline:true, //Allow items to show in-line or not.
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										allowdeletion:true,
									},
									{
										rendertype:"kvrendertags",
										dbfield:"damagedconditions", //When we go to save the item, we'll need to have an idea of children!
										label:"Damaged:",
										tagkey:"ConditionText", //Human readable value/column - the thing we type in and change on the child row. "ConditionText"
										defaultstyle:{borderRadius:3, color:"#FFF"},
										conditionalstyles:[
											{key:"ConditionType", value:"Damaged", backgroundColor:"#D9534F", color:"#FFF", border:"1px solid #D43F3A"},
										],
										inline:true, //Allow items to show in-line or not.
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										allowdeletion:true,
									},
									{
										rendertype:"kvheader",
										dbfield:"",
										label:"Add Conditions",
										labelwidth:170,
										padding:"20px 0px 0px 0px",
										labeltextAlign:"center",
										valuetextAlign:"left",
									},
									{
										rendertype:"kvaddtags",
										dbfield:"minorconditions", //When we go to save the item, we'll need to have an idea of children!
										label:"Minor:",
										//This is the human readable text tag we're adding. This particular one is mapped to child table column "Name".
										tagkey:"ConditionText",
										//Next are default mappings. Here we can add values like "ProductID", "AssetType", "ConditionType" that help link tags to other tables or statues.
										defaultmappings: [
											{ key: "ItemID", from: "ID" },         // dynamic from localstate.itemdata.ID
											{ key: "ConditionType", value: "Minor" } // hardcoded
										],
										inline: true, //Allow items to show in-line or not.
										placeholder:"Add condition and hit enter.",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
									{
										rendertype:"kvaddtags",
										dbfield:"majorconditions", //When we go to save the item, we'll need to have an idea of children!
										label:"Major:",
										//This is the human readable text tag we're adding. This particular one is mapped to child table column "Name".
										tagkey:"ConditionText",
										//Next are default mappings. Here we can add values like "ProductID", "AssetType", "ConditionType" that help link tags to other tables or statues.
										defaultmappings: [
											{ key: "ItemID", from: "ID" },         // dynamic from localstate.itemdata.ID
											{ key: "ConditionType", value: "Major" } // hardcoded
										],
										inline: true, //Allow items to show in-line or not.
										placeholder:"Add condition and hit enter.",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
									{
										rendertype:"kvaddtags",
										dbfield:"damagedconditions", //When we go to save the item, we'll need to have an idea of children!
										label:"Damaged:",
										//This is the human readable text tag we're adding. This particular one is mapped to child table column "Name".
										tagkey:"ConditionText",
										//Next are default mappings. Here we can add values like "ProductID", "AssetType", "ConditionType" that help link tags to other tables or statues.
										defaultmappings: [
											{ key: "ItemID", from: "ID" },         // dynamic from localstate.itemdata.ID
											{ key: "ConditionType", value: "Damaged" } // hardcoded
										],
										inline: true, //Allow items to show in-line or not.
										placeholder:"Add condition and hit enter.",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
								]
							}
						]
					}
				]
			},

			{
				rendertype: "section",
				title: "Specifications",
				description: "",
				children: [
					{
						rendertype: "gridcontainer",
						children: [
							{
								rendertype: "griditem",
								size: { xs: 12, sm: 12, md: 6, lg: 6 },
								children: [
									{
										rendertype: "kvinputtext",
										dbfield: "CPU",
										label: "CPU:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										}
									},
									{
										rendertype: "kvinputtext",
										dbfield: "RAM",
										label: "RAM:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										}
									},
									{
										rendertype: "kvinputtext",
										dbfield: "HDD1Size",
										label: "HDD 1:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										}
									},
									{
										rendertype: "kvinputtext",
										dbfield: "HDD2Size",
										label: "HDD 2:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										}
									},
									{
										rendertype: "kvinputtext",
										dbfield: "Optical1Desc",
										label: "Optical:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										}
									},
									{
										rendertype: "kvinputtext",
										dbfield: "Batt1",
										label: "Batt 1:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										}
									},
									{
										rendertype: "kvinputtext",
										dbfield: "Batt2",
										label: "Batt 2:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										}
									},
									{
										rendertype: "kvinputtext",
										dbfield: "Network1",
										label: "Network 1:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										}
									},
									{
										rendertype: "kvinputtext",
										dbfield: "Network2",
										label: "Network 2:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										}
									},
									{
										rendertype: "kvinputtext",
										dbfield: "Network3",
										label: "Network 3:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										}
									},
								]
							},
							{
								rendertype: "griditem",
								size: { xs: 12, sm: 12, md: 6, lg: 6 },
								children: [
									{
										rendertype:"kvstaticselect",
										dbfield:"Webcam",
										label:"Webcam:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										values:[
											//Build in any blank string options here:
											{ optionlabel:"-", value:null}, //DB may or may not like this.
											{ optionlabel:"Yes", value: 1},
											{ optionlabel:"No", value:0}
										],
										validation:{
											type:"select",
											maxlength:"10",
											minlength:"1"
										}
									},
									{
										rendertype:"kvstaticselect",
										dbfield:"Battery",
										label:"Battery:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										values:[
											//Build in any blank string options here:
											{ optionlabel:"-", value:null}, //DB may or may not like this.
											{ optionlabel:"Yes", value: 1},
											{ optionlabel:"No", value:0}
										],
										validation:{
											type:"select",
											maxlength:"10",
											minlength:"1"
										}
									},
									{
										rendertype:"kvstaticselect",
										dbfield:"Touchscreen",
										label:"Touchscreen:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										values:[
											//Build in any blank string options here:
											{ optionlabel:"-", value:null}, //DB may or may not like this.
											{ optionlabel:"Yes", value: 1},
											{ optionlabel:"No", value:0}
										],
										validation:{
											type:"select",
											maxlength:"10",
											minlength:"1"
										}
									},
									{
										rendertype:"kvstaticselect",
										dbfield:"Bluetooth",
										label:"Bluetooth:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										values:[
											//Build in any blank string options here:
											{ optionlabel:"-", value:null}, //DB may or may not like this.
											{ optionlabel:"Yes", value: 1},
											{ optionlabel:"No", value:0}
										],
										validation:{
											type:"select",
											maxlength:"10",
											minlength:"1"
										}
									},
									{
										rendertype: "kvinputtext",
										dbfield: "Resolution",
										label: "Resolution:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										}
									},
									{
										rendertype: "kvinputtext",
										dbfield: "Video1Desc",
										label: "Video 1:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										}
									},
									{
										rendertype: "kvinputtext",
										dbfield: "Video2Desc",
										label: "Video 2:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										validation: {
											type: "text",
											maxlength: "100",
											minlength: "1"
										}
									},
								
								]
							}
						]
					}
				]
			},


			{
				rendertype: "section",
				title: "Record Details ",
				description: "",
				children: [
					{
						rendertype: "gridcontainer",
						children: [
							{
								rendertype: "griditem",
								size: { xs: 12, sm: 12, md: 6, lg: 6 },
								children: [
									{
										rendertype: "kvstaticdatetime",
										dbfield: "created_at",
										label: "Created:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left"
									},
									{
										rendertype: "kvnesteduser",
										dbfield: "creationuser",
										label: "Created By:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left"
									},
									{
										rendertype: "kvcustomlink",
										dbfield: "lot.LotName",          // text shown to user
										label: "Lot:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left",
										linkstart: "/inventorymanager?LotName=",
										linkend: "lot.LotName"           // value appended to URL (encoded)
									},
									{
										rendertype:"kvstaticinputtext",
										dbfield:"lot.vendor.VendorName",
										label:"Vendor:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
									// {
									// 	rendertype:"kvstaticinputtext",
									// 	dbfield:"LastRepairDescription",
									// 	label:"Last Repair:",
									// 	labelwidth:170,
									// 	labeltextAlign:"right",
									// 	valuetextAlign:"left",
									// },
									// {
									// 	rendertype: "kvnesteduser",
									// 	dbfield: "lastrepairuser",
									// 	label: "Repaired By:",
									// 	labelwidth: 170,
									// 	labeltextAlign: "right",
									// 	valuetextAlign: "left"
									// },
								]
							},
							{
								rendertype: "griditem",
								size: { xs: 12, sm: 12, md: 6, lg: 6 },
								children: [
									{
										rendertype: "kvstaticdatetime",
										dbfield: "updated_at",
										label: "Last Update:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left"
									},
									{
										rendertype: "kvnesteduser",
										dbfield: "updateuser",
										label: "Updated By:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left"
									},
									{
										rendertype: "kvstaticdatetime",
										dbfield: "LastAudit",
										label: "Last Audit:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left"
									},
									{
										rendertype: "kvstaticdatetime",
										dbfield: "ReviewedDate",
										label: "Reviewed Date:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left"
									},
									{
										rendertype: "kvnesteduser",
										dbfield: "reviewuser",
										label: "Reviewed By:",
										labelwidth: 170,
										labeltextAlign: "right",
										valuetextAlign: "left"
									},
									{
										rendertype: "kvendbuttons",
										moreoptionsitems: {
											deletebutton: true,
											deletebuttonlabel: "Delete Item"
										},
										custombuttons: [
											{
												buttonlabel: "Mark as Reviewed",
												optionicon: PlaylistAddCheckIcon,
												renderdependencies:[
													{key:"Reviewed", value:"0"}
												],
												buttonFn: ({ localstate, UpdateState, element, perms, userName}) => {
													// Now you have full access to modify state from your config!
													localstate.itemdata.Reviewed = 1;
													localstate.itemdata.reviewuser = {
														placeholdervalue:userName
													};
													localstate.itemdata.ReviewedDate=new Date().toISOString();
													localstate.pendingupdate = true;
													UpdateState({ ...localstate });
												}
											}
										]

									}

								]
							}
						]
					}
				]
			},

			{
				rendertype: "minigrid",
				dbfield:"repairs",
				title: "Repairs",
				owneronly: true,
    			ownerfield: "CreatedByUserID",
    			primarykey: "ID",
				description: "",
				allowadd:true,
				addbuttonlabel:"Add Repair",
				allowdelete:true,
				deletebuttonlabel:"Delete Repair",


				defaultmappings: [
					{ key: "ItemID", from: "ItemID" },         // dynamic from localstate.itemdata.ID
					{ key: "RepairDescription", value: "" } // hardcoded
				],

				columns:[
					{
						rendertype:"minigriddatetime",
						dbfield:"created_at",
						label:"Creation Date",
						width:"270px",
						labeltextAlign:"left",
						valuetextAlign:"left",
					},
					{
						rendertype: "minigridstatictext",
						dbfield: "creationuser",
						label: "User",
						width:"auto",
						labeltextAlign: "left",
						valuetextAlign: "left",
					  
						// optional behavior
						format: "userfull",          // custom formatter hint
						fallback: "—",               // shown when null
					  },
					{
						rendertype:"minigridtextinput",
						dbfield:"RepairDescription",
						label:"Repair Description",
						placeholder:"Enter repair description",
						width:"auto",
						labeltextAlign:"left",
						valuetextAlign:"left",
						
						validation:{
							type:"text",
							maxlength: "128",
							minlength: "2"
						}
					},
					
				]

			},

			{
				rendertype: "minigrid",
				dbfield:"mdm",
				title: "Mobile Device Management",
				description: "",
				allowadd:true,
				addbuttonlabel:"Add MDM",
				allowdelete:true,
				deletebuttonlabel:"Delete MDM",


				defaultmappings: [
					{ key: "ItemID", from: "ItemID" },         // dynamic from localstate.itemdata.ID
					{ key: "MDMType", value: "" }, // hardcoded
					{ key: "MDMReported", value: 0 },
					{ key: "MDMCleared", value: 0 }
				],

				columns:[
					{
						rendertype:"minigriddatetime",
						dbfield:"created_at",
						label:"Creation Date",
						width:"auto",
						labeltextAlign:"left",
						valuetextAlign:"left",
					},
					{
						rendertype: "minigridstatictext",
						dbfield: "creationuser",
						label: "User",
						width:"auto",
						labeltextAlign: "left",
						valuetextAlign: "left",
					  
						// optional behavior
						format: "userfull",          // custom formatter hint
						fallback: "—",               // shown when null
					  },
					{
						rendertype:"minigridselect",
						dbfield:"MDMType",
						label:"MDM Type",
						placeholder:"MDM Type Selection",
						values:[
							{ optionlabel:"-", value:null}, //DB may or may not like this.
							{ optionlabel:"Intune", value: "Intune"},
							{ optionlabel:"PanGP", value:"PanGP"},
							{ optionlabel:"Absolute Persistence", value:"Absolute Persistence"}
						],
						//auto instead width:"180px",
						labeltextAlign:"left",
						valuetextAlign:"left",
					},
					{
						rendertype:"minigridcheckbox",
						dbfield:"MDMReported",
						label:"Reported",
						//auto instead width:"180px",
						labeltextAlign:"center",
						valuetextAlign:"center",
					},
					{
						rendertype:"minigridcheckbox",
						dbfield:"MDMCleared",
						label:"Cleared",
						//auto instead width:"180px",
						labeltextAlign:"center",
						valuetextAlign:"center",
					},
					
				]

			},
			{
				rendertype: "minigrid",
				dbfield:"manifests",
				title: "Manifests",
				description: "",
				allowadd:false,
				addbuttonlabel:"",
				allowdelete:false,
				deletebuttonlabel:"",

				//Only used for adding items:
				defaultmappings: [
					{ key: "AssetSerialNumber", from: "SerialNumber" },         // dynamic from localstate.itemdata.ID
					{ key: "InternalOrderID", value: "" } // hardcoded
				],

				columns: [
					{
						rendertype: "minigridcustomlink",
						dbfield: "order.OrderID",          // text shown to user
						label: "Order",
						labelwidth: 170,
						labeltextAlign: "left",
						valuetextAlign: "left",
						linkstart: "/order/",
						linkend: "order.InternalID"           // value appended to URL (encoded)
					},

					{
						//Use for nested values that have renderdependencies
						rendertype: "minigridsmartvalue",
						//Render formats should attempt to fill the requirement and add values concatenated by a space!
						//Useful for names or if you want to spit out added data.
						renderformats:[
							{requires:"order.BFullName", renderitems:[
								'order.BFullName'
							]},
							//If it can't fill order.BFullName because it's blank or null, move on to the next format!
							{requires:"order.BFirstName", renderitems:[
								'order.BFirstName', 'order.BLastName'
							]},
							{requires:"order.SFirstName", renderitems:[
								'order.SFirstName', 'order.SLastName'
							]},
							//Render logic should add a "-" for when the values can't be derived.
						],
						
						label: "Name",
						width:"auto",
						labeltextAlign: "left",
						valuetextAlign: "left",
					},
					{
						rendertype:"minigriddatetime",
						dbfield:"order.OrderDate",
						label:"Order Date",
						width:"auto",
						labeltextAlign:"left",
						valuetextAlign:"left",
					},
					{
						rendertype: "minigridstatictext",
						dbfield: "Valid",
						label: "Bound to Order",
						renderyesnow:true,
						width:"150px",
						labeltextAlign: "center",
						valuetextAlign: "center",
					  },
					
				]

			},
			{
				rendertype: "kvaccordionsplit",
				title: "Specs",
				sourcepath: "specs",
				defaultopen: true,
				// titlebuilder: (spec, idx, formatDateLabel) => {
				// 	const created = spec?.created_at || spec?.createdAt || spec?.CreatedDate;
				// 	return `Specs: ${created ? formatDateLabel(created) : `Spec ${idx + 1}`}`;
				// },
				titlebuilder: (spec, index, formatDateLabel) => {
					const step = spec?.SpecStep || "Specs";
					const created = spec?.created_at || spec?.createdAt || spec?.CreatedDate;
					const datePart = created ? formatDateLabel(created) : `Entry ${index + 1}`;
					return `${step}: ${datePart}`;
				},

				leftside: [
					{ key: "FlexbuntuVersion", label: "Flex Version:" },
					{ key: "Manufacturer", label: "Mfg:" },
					{ key: "Model", label: "Model:" },
					{ key: "CPU", label: "CPU:" },
					{ key: "TotalRAM", label: "Total RAM:" },
					{ key: "Video1Desc", label: "Video 1:", renderdependency:"Video1Desc" },
					{ key: "Video2Desc", label: "Video 2:", renderdependency:"Video2Desc" },
					{ key: "Resolution", label: "Resolution:", renderdependency:"Resolution" },
					{ key: "HorizontalResolution", label: "Horizontal Res:", renderdependency:"HorizontalResolution" },
					{ key: "VerticalResolution", label: "Vertical Res:", renderdependency:"VerticalResolution" },




					//HDD1
					{ key: "HDD1Size", label: "Capacity:", renderdependency:"HDD1Size", header: "Hard Drive 1 Data" },
					{ key: "HDD1Model", label: "Model:", renderdependency:"HDD1Size"},
					{ key: "HDD1SerialNumber", label: "Serial Number:", renderdependency:"HDD1Size"},
					{ key: "HDD1InterfaceType", label: "Interface:", renderdependency:"HDD1Size"},
					{ key: "HDD1SMARTStatus", label: "Smart Status:", renderdependency:"HDD1Size"},
					{ key: "HDD1ReallocatedSectors", label: "Reallocated:", renderdependency:"HDD1Size"},
					{ key: "HDD1PendingSectors", label: "Pending:", renderdependency:"HDD1Size"},
					{ key: "HDD1Temp", label: "Temp:", renderdependency:"HDD1Size"},
					{ key: "HDD1PowerOnHours", label: "Hours:", renderdependency:"HDD1Size"},
					//HDD2
					{ key: "HDD2Size", label: "Capacity:", renderdependency:"HDD2Size", header: "Hard Drive 2 Data" },
					{ key: "HDD2Model", label: "Model:", renderdependency:"HDD2Size"},
					{ key: "HDD2SerialNumber", label: "Serial Number:", renderdependency:"HDD2Size"},
					{ key: "HDD2InterfaceType", label: "Interface:", renderdependency:"HDD2Size"},
					{ key: "HDD2SMARTStatus", label: "Smart Status:", renderdependency:"HDD2Size"},
					{ key: "HDD2ReallocatedSectors", label: "Reallocated:", renderdependency:"HDD2Size"},
					{ key: "HDD2PendingSectors", label: "Pending:", renderdependency:"HDD2Size"},
					{ key: "HDD2Temp", label: "Temp:", renderdependency:"HDD2Size"},
					{ key: "HDD2PowerOnHours", label: "Hours:", renderdependency:"HDD2Size"},
					//HDD3
					{ key: "HDD3Size", label: "Capacity:", renderdependency:"HDD3Size", header: "Hard Drive 1 Data" },
					{ key: "HDD3Model", label: "Model:", renderdependency:"HDD3Size"},
					{ key: "HDD3SerialNumber", label: "Serial Number:", renderdependency:"HDD3Size"},
					{ key: "HDD3InterfaceType", label: "Interface:", renderdependency:"HDD3Size"},
					{ key: "HDD3SMARTStatus", label: "Smart Status:", renderdependency:"HDD3Size"},
					{ key: "HDD3ReallocatedSectors", label: "Reallocated:", renderdependency:"HDD3Size"},
					{ key: "HDD3PendingSectors", label: "Pending:", renderdependency:"HDD3Size"},
					{ key: "HDD3Temp", label: "Temp:", renderdependency:"HDD3Size"},
					{ key: "HDD3PowerOnHours", label: "Hours:", renderdependency:"HDD3Size"},
					//HDD4
					{ key: "HDD4Size", label: "Capacity:", renderdependency:"HDD4Size", header: "Hard Drive 1 Data" },
					{ key: "HDD4Model", label: "Model:", renderdependency:"HDD4Size"},
					{ key: "HDD4SerialNumber", label: "Serial Number:", renderdependency:"HDD4Size"},
					{ key: "HDD4InterfaceType", label: "Interface:", renderdependency:"HDD4Size"},
					{ key: "HDD4SMARTStatus", label: "Smart Status:", renderdependency:"HDD4Size"},
					{ key: "HDD4ReallocatedSectors", label: "Reallocated:", renderdependency:"HDD4Size"},
					{ key: "HDD4PendingSectors", label: "Pending:", renderdependency:"HDD4Size"},
					{ key: "HDD4Temp", label: "Temp:", renderdependency:"HDD4Size"},
					{ key: "HDD4PowerOnHours", label: "Hours:", renderdependency:"HDD4Size"},

					//Networks *Should* populate by Network1 first... So go ahead and place header on network1
					{ key: "Network1", label: "Device 1:", renderdependency:"Network1", header: "Network" },
					{ key: "Network2", label: "Device 2:", renderdependency:"Network2"},
					{ key: "Network3", label: "Device 3:", renderdependency:"Network3"},
					{ key: "Network4", label: "Device 4:", renderdependency:"Network4"},
					{ key: "Network5", label: "Device 5:", renderdependency:"Network5"},
					{ key: "Network6", label: "Device 6:", renderdependency:"Network6"},


				],

				rightside: [
					{ key: "SMBIOSVersion", label: "BIOS:" },
					{ key: "OS", label: "OS:" },
					{ key: "WindowsProductKey", label: "Win Key:" },
					{ key: "WindowsBiosKey", label: "Win BIOS Key:" },
					{ key: "OSVersion", label: "OS Version:" },
					{ key: "ClientIP", label: "Client IP:" },
					
					//Tests - Right now Flexbuntu will report ChecklistSpecs, ChecklistSound, etc.... ChecklistSpecs is good enough to require all test results:
					{ key: "ChecklistSpecs", label: "Specs:", renderdependency:"ChecklistSpecs", header: "Checklists", format:"boolicon" },
					{ key: "ChecklistMissingDevices", label: "Drivers:", renderdependency:"ChecklistSpecs", format:"boolicon" },
					{ key: "ChecklistSound", label: "Sound:", renderdependency:"ChecklistSpecs", format:"boolicon" },
					{ key: "ChecklistKeyboard", label: "Keyboard:", renderdependency:"ChecklistSpecs", format:"boolicon" },
					{ key: "ChecklistWifi", label: "Wifi:", renderdependency:"ChecklistSpecs", format:"boolicon" },
					{ key: "ChecklistBattery", label: "Battery:", renderdependency:"ChecklistSpecs", format:"boolicon" },
					{ key: "ChecklistStress", label: "Stress:", renderdependency:"ChecklistSpecs", format:"boolicon" },
					{ key: "ChecklistActivation", label: "Activation:", renderdependency:"ChecklistSpecs", format:"boolicon" },
					{ key: "ChecklistWebcam", label: "Webcam:", renderdependency:"ChecklistSpecs", format:"boolicon" },
					{ key: "ChecklistTouchscreen", label: "Touchscreen:", renderdependency:"ChecklistSpecs", format:"boolicon" },
					{ key: "KeysHit", label: "Keys Hit:", renderdependency:"ChecklistSpecs" },

					//RAM1
					{ key: "RAM1Capacity", label: "Capacity:", renderdependency:"RAM1Capacity", header: "RAM 1 Details"},
					{ key: "RAM1Manufacturer", label: "Mfg:", renderdependency:"RAM1Capacity"},
					{ key: "RAM1Model", label: "Model:", renderdependency:"RAM1Capacity"},
					{ key: "RAM1SerialNumber", label: "Serial Number:", renderdependency:"RAM1Capacity"},
					{ key: "RAM1Speed", label: "Speed:", renderdependency:"RAM1Capacity"},

					//RAM2
					{ key: "RAM2Capacity", label: "Capacity:", renderdependency:"RAM2Capacity", header: "RAM 2 Details"},
					{ key: "RAM2Manufacturer", label: "Mfg:", renderdependency:"RAM2Capacity"},
					{ key: "RAM2Model", label: "Model:", renderdependency:"RAM2Capacity"},
					{ key: "RAM2SerialNumber", label: "Serial Number:", renderdependency:"RAM2Capacity"},
					{ key: "RAM2Speed", label: "Speed:", renderdependency:"RAM2Capacity"},

					//RAM3
					{ key: "RAM3Capacity", label: "Capacity:", renderdependency:"RAM3Capacity", header: "RAM 3 Details"},
					{ key: "RAM3Manufacturer", label: "Mfg:", renderdependency:"RAM3Capacity"},
					{ key: "RAM3Model", label: "Model:", renderdependency:"RAM3Capacity"},
					{ key: "RAM3SerialNumber", label: "Serial Number:", renderdependency:"RAM3Capacity"},
					{ key: "RAM3Speed", label: "Speed:", renderdependency:"RAM3Capacity"},

					//RAM4
					{ key: "RAM4Capacity", label: "Capacity:", renderdependency:"RAM4Capacity", header: "RAM 4 Details"},
					{ key: "RAM4Manufacturer", label: "Mfg:", renderdependency:"RAM4Capacity"},
					{ key: "RAM4Model", label: "Model:", renderdependency:"RAM4Capacity"},
					{ key: "RAM4SerialNumber", label: "Serial Number:", renderdependency:"RAM4Capacity"},
					{ key: "RAM4Speed", label: "Speed:", renderdependency:"RAM4Capacity"},

					//RAM5
					{ key: "RAM5Capacity", label: "Capacity:", renderdependency:"RAM5Capacity", header: "RAM 5 Details"},
					{ key: "RAM5Manufacturer", label: "Mfg:", renderdependency:"RAM5Capacity"},
					{ key: "RAM5Model", label: "Model:", renderdependency:"RAM5Capacity"},
					{ key: "RAM5SerialNumber", label: "Serial Number:", renderdependency:"RAM5Capacity"},
					{ key: "RAM5Speed", label: "Speed:", renderdependency:"RAM5Capacity"},

					//RAM6
					{ key: "RAM6Capacity", label: "Capacity:", renderdependency:"RAM6Capacity", header: "RAM 6 Details"},
					{ key: "RAM6Manufacturer", label: "Mfg:", renderdependency:"RAM6Capacity"},
					{ key: "RAM6Model", label: "Model:", renderdependency:"RAM6Capacity"},
					{ key: "RAM6SerialNumber", label: "Serial Number:", renderdependency:"RAM6Capacity"},
					{ key: "RAM6Speed", label: "Speed:", renderdependency:"RAM6Capacity"},

					//RAM7
					{ key: "RAM7Capacity", label: "Capacity:", renderdependency:"RAM7Capacity", header: "RAM 7 Details"},
					{ key: "RAM7Manufacturer", label: "Mfg:", renderdependency:"RAM7Capacity"},
					{ key: "RAM7Model", label: "Model:", renderdependency:"RAM7Capacity"},
					{ key: "RAM7SerialNumber", label: "Serial Number:", renderdependency:"RAM7Capacity"},
					{ key: "RAM7Speed", label: "Speed:", renderdependency:"RAM7Capacity"},

					//RAM8
					{ key: "RAM8Capacity", label: "Capacity:", renderdependency:"RAM8Capacity", header: "RAM 8 Details"},
					{ key: "RAM8Manufacturer", label: "Mfg:", renderdependency:"RAM8Capacity"},
					{ key: "RAM8Model", label: "Model:", renderdependency:"RAM8Capacity"},
					{ key: "RAM8SerialNumber", label: "Serial Number:", renderdependency:"RAM8Capacity"},
					{ key: "RAM8Speed", label: "Speed:", renderdependency:"RAM8Capacity"},



					{ key: "ChecklistMissingDevices", label: "Drivers:", renderdependency:"ChecklistSpecs", format:"boolicon" },
					{ key: "ChecklistMissingDevices", label: "Drivers:", renderdependency:"ChecklistSpecs", format:"boolicon" },
				]
			}
			// 			{
// 				rendertype: "kvaccordionobject",
// 				title: "Specs",
// 				sourcepath: "specs",  // or "specs"
// 				defaultopen: false,
// 				titlebuilder: (spec, index, formatDateLabel) => {
//     const created = spec?.created_at || spec?.createdAt || spec?.CreatedDate;
//     return `Specs: ${created ? formatDateLabel(created) : `Spec ${index + 1}`}`;
//   },
// 				fields: [
// 				  "CPU",
// 				  "CPUCores",
// 				  "TotalRAM",
// 				  "HDD1Size",
// 				  "RAM1Capacity",
// 				  "Resolution",
// 				  "Webcam",
// 				  "TouchscreenBool",
// 				  "BluetoothBool",
// 				  "Network1",
// 				  "Network2"
// 				],
// 				labels: {
// 				  CPU: "CPU",
// 				  CPUCores: "CPU Cores",
// 				  TotalRAM: "RAM",
// 				  HDD1Size: "HDD",
// 				  RAM1Capacity: "RAM 1",
// 				  Resolution: "Resolution",
// 				  Webcam: "Webcam",
// 				  TouchscreenBool: "Touchscreen",
// 				  BluetoothBool: "Bluetooth",
// 				  Network1: "Network 1",
// 				  Network2: "Network 2"
// 				}
// 			  }

		]
	};


	// ----------------------------
	// State
	// ----------------------------
	const [state, setState] = useState({
		dbreload: true,
		itemloaded: false,
		pendingupdate: false,
		itemdata: {},
		itemdatadefaults: {},
		deleteitem: false,
		skipmessage:false,
	});

	const localstate = Object.assign({}, state);

	const UpdateState = (newState) => {
		setState(newState);
	};

	// ----------------------------
	// Load lifecycle
	// ----------------------------
	useEffect(() => {
		document.title = "Inventory Item";
		dispatch(setCurrentMenuSection(configuration.menusection));
		dispatch(setCurrentMenuItem(configuration.menuitem));

		if (state.dbreload) {
			localstate.dbreload = false;
			if (localstate.skipmessage){
				LoadItem(localstate, UpdateState, configuration, dispatch);
			} else {
				LoadItem(localstate, UpdateState, configuration, dispatch);
			}
			
		}
	}, [state]);

	// ----------------------------
	// Optional custom screen logic after load
	// ----------------------------
	useEffect(() => {
		if (localstate.itemloaded) {
			UpdateDerivedFields({ localstate });
		}
	}, [localstate.itemloaded, localstate.itemdata]);

	// ----------------------------
	// Optional extra custom view block
	// ----------------------------
	const CustomInventorySummary = () => {
		return (
			<div style={{ margin: "20px auto", maxWidth: "1200px", backgroundColor: "#FFF", border: "1px solid #DDD", padding: "12px" }}>
				<h3 style={{ margin: "0 0 12px 0" }}>Additional Inventory Details</h3>
				<div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
					<div>
						<b>Condition:</b> {localstate.itemdata.Condition || "—"}
					</div>
					<div>
						<b>Model:</b> {localstate.itemdata.Model || "—"}
					</div>
					<div>
						<b>Serial:</b> {localstate.itemdata.SerialNumber || "—"}
					</div>
				</div>
			</div>
		);
	};


	return (
		<div style={{ backgroundColor: "#FFF", borderLeft: "1px solid #AAA", minHeight: "calc(100vh - 35px)" }}>
			<FlexiItemTopMenu
				localstate={localstate}
				UpdateState={UpdateState}
				classes={classes}
				configuration={configuration}
				SetView={SetView}
				isChild={isChild}
				backlinktext={backlinktext}
				dispatch={dispatch}
				history={history}
				perms={perms}
			/>

			<div
				key="itemcontainer"
				style={{
					margin: "auto",
					maxWidth: configuration.maxwidth,
					textAlign: "center",
					fontFamily: "Inter",
					fontOpticalSizing: "auto",
					fontWeight: 350
				}}
			>
				{localstate.itemloaded && (
					<>
						<FlexiItemHeader
							headerprepend={configuration.headerprepend}
							headerappend={configuration.headerappend}
							headervalue={configuration.headervalue}
							localstate={localstate}
						/>

						<FlexiItemContent
							configuration={configuration}
							localstate={localstate}
							UpdateState={UpdateState}
							classes={classes}
							perms={perms}
							dispatch={dispatch}
						/>


						{/* <CustomInventorySummary /> */}
					</>
				)}
			</div>
		</div>
	);
};

export default FlexiInvItem;